import { API_URLS } from '@chessx/shared/api-routes'
import React, { CSSProperties } from 'react'
import './Dashboard.scss'
import axiosInstance from '../../network/axiosInstance'
import { toastErrorMixin, toastMixin } from '../../utils/interact'

type SyncCapabilities = {
  environment: 'prod' | 'stage' | 'unknown'
  contentSync: boolean
  assetSync: boolean
}

const Dashboard = () => {
  const [syncing, setSyncing] = React.useState(false)
  const [syncingReset, setSyncingReset] = React.useState(false)
  const [syncingContent, setSyncingContent] = React.useState(false)
  const [syncingAssets, setSyncingAssets] = React.useState(false)
  const [capabilities, setCapabilities] = React.useState<SyncCapabilities | null>(null)

  React.useEffect(() => {
    let cancelled = false
    const loadCapabilities = async () => {
      try {
        const res = await axiosInstance.get(API_URLS.ADMIN_SYNC_CAPABILITIES)
        if (!cancelled) setCapabilities(res.data as SyncCapabilities)
      } catch {
        if (!cancelled)
          setCapabilities({ environment: 'unknown', contentSync: false, assetSync: false })
      }
    }
    loadCapabilities()
    return () => {
      cancelled = true
    }
  }, [])

  const syncAssetsFromStage = async () => {
    try {
      setSyncingAssets(true)
      const res = await axiosInstance.put(API_URLS.ADMIN_SYNC_ASSETS_FROM_SOURCE)
      if (res.status === 200) {
        const copied = (res.data?.copied?.length ?? 0) as number
        toastMixin.fire({
          title: copied === 0 ? 'Assets already in sync' : `Copied ${copied} asset(s) from stage`
        })
      }
    } catch (err) {
      toastErrorMixin.fire({ title: 'Error syncing assets from stage.' })
    } finally {
      setSyncingAssets(false)
    }
  }

  const syncContentFromStage = async () => {
    try {
      setSyncingContent(true)
      const res = await axiosInstance.put(API_URLS.ADMIN_SYNC_CONTENT_FROM_SOURCE)
      if (res.status === 200) {
        toastMixin.fire({
          title: `Promoted ${res.data?.totalDocuments ?? 0} document(s) from stage`
        })
      }
    } catch (err) {
      toastErrorMixin.fire({ title: 'Error promoting content from stage.' })
    } finally {
      setSyncingContent(false)
    }
  }

  const syncAssetUrls = async () => {
    try {
      setSyncing(true)
      const url = API_URLS.ADMIN_URLS_COMPARE
      const res = await axiosInstance.put(url)
      if (res.status === 200) {
        toastMixin.fire({ title: 'Sync Successful' })
      }
    } catch (err) {
      toastErrorMixin.fire({ title: 'Error syncing asset URLs.' })
    } finally {
      setSyncing(false)
    }
  }

  const syncResetAllUsers = async () => {
    try {
      setSyncingReset(true)
      const url = API_URLS.ADMIN_RESET_ALL_USERS
      const res = await axiosInstance.put(url)
      if (res.status === 200) {
        toastMixin.fire({ title: 'Reset All Users Successful' })
      }
    } catch (err) {
      toastErrorMixin.fire({ title: 'Error resetting all users.' })
    } finally {
      setSyncingReset(false)
    }
  }

  return (
    <div className="dashboard-page">
      <div className="dashboard-welcome">
        <h3 className="welcome-title">Welcome back, Admin</h3>
        <p className="welcome-sub">Manage your ChessX platform from here.</p>
      </div>

      <div className="dashboard-cards">
        <div className="action-card">
          <div className="action-card-icon sync-icon">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="23 4 23 10 17 10" />
              <polyline points="1 20 1 14 7 14" />
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
            </svg>
          </div>
          <div className="action-card-body">
            <h4 className="action-card-title">Sync Asset URLs</h4>
            <p className="action-card-desc">
              Compare and update all asset URLs to ensure they are current and valid.
            </p>
          </div>
          <button className="action-card-btn primary" disabled={syncing} onClick={syncAssetUrls}>
            {syncing ? (
              <>
                <span className="btn-spinner" />
                Syncing...
              </>
            ) : (
              'Sync Now'
            )}
          </button>
        </div>

        {capabilities?.assetSync && (
          <div className="action-card">
            <div className="action-card-icon sync-icon">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            </div>
            <div className="action-card-body">
              <h4 className="action-card-title">Sync Assets from Stage</h4>
              <p className="action-card-desc">
                Copy images from the stage asset bucket into production. Only new or changed files
                are transferred.
              </p>
            </div>
            <button
              className="action-card-btn primary"
              disabled={syncingAssets}
              onClick={syncAssetsFromStage}
            >
              {syncingAssets ? (
                <>
                  <span className="btn-spinner" />
                  Syncing...
                </>
              ) : (
                'Sync Assets'
              )}
            </button>
          </div>
        )}

        {capabilities?.contentSync && (
          <div className="action-card">
            <div className="action-card-icon reset-icon">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <ellipse cx="12" cy="5" rx="9" ry="3" />
                <path d="M3 5v14a9 3 0 0 0 18 0V5" />
                <path d="M3 12a9 3 0 0 0 18 0" />
              </svg>
            </div>
            <div className="action-card-body">
              <h4 className="action-card-title">Promote Content from Stage</h4>
              <p className="action-card-desc">
                Replace production content collections with the stage copies. Existing production
                content in these collections is overwritten.
              </p>
            </div>
            <button
              className="action-card-btn primary"
              disabled={syncingContent}
              onClick={syncContentFromStage}
            >
              {syncingContent ? (
                <>
                  <span className="btn-spinner" />
                  Promoting...
                </>
              ) : (
                'Promote Content'
              )}
            </button>
          </div>
        )}

        <div className="action-card">
          <div className="action-card-icon reset-icon">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
              <path d="M3 3v5h5" />
            </svg>
          </div>
          <div className="action-card-body">
            <h4 className="action-card-title">Reset All Users</h4>
            <p className="action-card-desc">
              Reset all user accounts to their default state. This action cannot be undone.
            </p>
          </div>
          <button
            className="action-card-btn danger"
            disabled={syncingReset}
            onClick={syncResetAllUsers}
          >
            {syncingReset ? (
              <>
                <span className="btn-spinner" />
                Resetting...
              </>
            ) : (
              'Reset All'
            )}
          </button>
        </div>
      </div>

      <div className="dashboard-quick-links">
        <h4 className="section-title">Quick Navigation</h4>
        <div className="quick-links-grid">
          {[
            { label: 'Manage Users', path: '/admin/users', icon: '👥', color: '#3b82f6' },
            { label: 'Heroes', path: '/admin/heroes', icon: '🛒', color: '#10b981' },
            { label: 'Chess Engine', path: '/admin/chess-apis', icon: '📊', color: '#ef4444' },
            { label: 'Tournaments', path: '/admin/tournaments', icon: '⚔️', color: '#6366f1' }
          ].map(link => (
            <a
              key={link.path}
              href={link.path}
              className="quick-link-card"
              style={{ '--link-color': link.color } as CSSProperties}
            >
              <span className="quick-link-icon">{link.icon}</span>
              <span className="quick-link-label">{link.label}</span>
            </a>
          ))}
        </div>
      </div>
    </div>
  )
}

export default Dashboard
